import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { formatDateWithTimezone } from '../utils/dateUtils';
import QuickAddLead from '../components/common/QuickAddLead';
import ImportLeadsModal from '../components/common/ImportLeadsModal';
import EmptyState from '../components/common/EmptyState';
import ErrorState from '../components/common/ErrorState';

const Leads = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const { isDark } = useTheme();

    // State
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Sort State — default to created_at:desc so latest leads appear first
    const [sortConfig, setSortConfig] = useState({ column: 'created_at', order: 'desc' });

    // Filters State
    const [filters, setFilters] = useState({
        search: searchParams.get('search') || '',
        status: '',
        source: '',
        dateRange: 'last30days',
        hotOnly: false
    });

    // Pagination State
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalLeads: 0,
        perPage: 10
    });

    // Debounce Search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLeads();
        }, 500); // 500ms debounce
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.currentPage, sortConfig]);

    const fetchLeads = async () => {
        try {
            setLoading(true);
            setError(null);

            // Calculate date range
            let start_date;
            const now = new Date();
            switch (filters.dateRange) {
                case 'today':
                    start_date = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
                    break;
                case 'last7days':
                    start_date = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
                    break;
                case 'last30days':
                    start_date = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
                    break;
                case 'last90days':
                    start_date = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
                    break;
                default: // 'all'
                    start_date = undefined;
            }

            const params = {
                page: pagination.currentPage,
                limit: pagination.perPage,
                search: filters.search || undefined,
                status: filters.status || undefined,
                source_platform: filters.source || undefined,
                start_date,
                sort: `${sortConfig.column}:${sortConfig.order}`,
                score_tier: filters.hotOnly ? 'hot' : undefined,
            };

            // Call API
            const response = await leadsAPI.getLeads(params);

            // Handle different potential response structures
            const leadsData = response.data?.leads || response.data || [];
            const meta = response.data?.pagination || {
                page: 1,
                totalPages: 1,
                total: leadsData.length
            };

            setLeads(leadsData);
            setPagination(prev => ({
                ...prev,
                currentPage: meta.page,
                totalPages: meta.totalPages,
                totalLeads: meta.total
            }));

        } catch (err) {
            console.error('Error fetching leads:', err);
            setError(err.message || 'Failed to load leads. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    // Handlers
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to page 1 on filter change
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    // Delete a single lead
    const handleDeleteLead = async (leadId, leadName) => {
        if (!window.confirm(`Are you sure you want to delete "${leadName || 'this lead'}"?`)) return;
        try {
            await leadsAPI.deleteLead(leadId);
            setLeads(prev => prev.filter(l => (l.id || l._id) !== leadId));
            setSelectedLeads(prev => prev.filter(id => id !== leadId));
            setPagination(prev => ({ ...prev, totalLeads: prev.totalLeads - 1 }));
        } catch (err) {
            console.error('Error deleting lead:', err);
            alert('Failed to delete lead. Please try again.');
        }
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} lead(s)? This cannot be undone.`)) return;
        try {
            await leadsAPI.deleteLeads(selectedLeads);
            setSelectedLeads([]);
            fetchLeads(); // Refresh the list
        } catch (err) {
            console.error('Error bulk deleting:', err);
            alert('Failed to delete leads. Please try again.');
        }
    };

    // Bulk mark as contacted
    const handleBulkMarkContacted = async () => {
        try {
            await Promise.all(selectedLeads.map(id => leadsAPI.updateLeadStatus(id, 'contacted')));
            setSelectedLeads([]);
            fetchLeads();
        } catch (err) {
            console.error('Error updating leads:', err);
            alert('Failed to update leads.');
        }
    };

    // Bulk export as CSV
    const handleBulkExport = (e) => {
        if (e) e.preventDefault();
        const selectedData = leads.filter(l => selectedLeads.includes(l.id || l._id));
        const csvRows = [
            ['Name', 'Email', 'Phone', 'Source', 'Status', 'Property Address', 'Date'].join(','),
            ...selectedData.map(l => [
                `"${l.lead_name || ''}"`,
                `"${l.lead_email || ''}"`,
                `"${l.lead_phone || ''}"`,
                `"${l.source_platform || ''}"`,
                `"${l.status || ''}"`,
                `"${l.property_address || ''}"`,
                `"${l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}"`,
            ].join(','))
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Manual refresh
    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchLeads();
        setIsRefreshing(false);
    };



    const toggleSelection = (leadId) => {
        setSelectedLeads(prev =>
            prev.includes(leadId)
                ? prev.filter(id => id !== leadId)
                : [...prev, leadId]
        );
    };

    const handleSort = (column) => {
        setSortConfig(prev => ({
            column,
            order: prev.column === column && prev.order === 'desc' ? 'asc' : 'desc'
        }));
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.column !== column) {
            return <span className="material-symbols-outlined text-[14px] opacity-30 ml-1">unfold_more</span>;
        }
        return (
            <span className="material-symbols-outlined text-[14px] text-primary dark:text-cyan ml-1">
                {sortConfig.order === 'asc' ? 'arrow_upward' : 'arrow_downward'}
            </span>
        );
    };

    const toggleAllSelection = () => {
        if (selectedLeads.length === leads.length) {
            setSelectedLeads([]);
        } else {
            setSelectedLeads(leads.map(l => l.id || l._id));
        }
    };

    // Helper functions for UI
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'new': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
            case 'contacted': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'replied': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'qualified': return 'bg-cyan/10 text-cyan border-cyan/20';
            case 'closed': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
            case 'lost': return 'bg-red-500/10 text-red-500 border-red-500/20';
            default: return 'bg-white/5 text-gray-400 border-white/10';
        }
    };

    const getScoreBadgeStyle = (tier) => {
        switch (tier) {
            case 'hot': return 'bg-red-500/10 text-red-400 border-red-500/30 shadow-red-500/10';
            case 'warm': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30 shadow-yellow-500/10';
            case 'cold': return 'bg-blue-500/10 text-blue-400 border-blue-500/30 shadow-blue-500/10';
            default: return 'bg-white/5 text-gray-400 border-white/10';
        }
    };

    const getSourceIcon = (source) => {
        switch (source?.toLowerCase()) {
            case 'zillow': return { icon: 'home', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' };
            case 'realtor': return { icon: 'real_estate_agent', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' };
            case 'whatsapp': return { icon: 'chat', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' };
            case 'facebook': return { icon: 'public', color: 'text-blue-600', bg: 'bg-blue-600/10 border-blue-600/20' };
            case 'website':
            case 'web': return { icon: 'language', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' };
            default: return { icon: 'link', color: 'text-gray-400', bg: 'bg-white/5 border-white/10' };
        }
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    const formatResponseTime = (seconds) => {
        if (!seconds) return '-';
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
        return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    };

    // Render Logic
    return (
        <div className="bg-mint dark:bg-navy min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6 md:mb-8">
                <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">Leads</h2>
                    <p className="text-darkTeal/60 dark:text-light/60 text-sm text-left">Manage and optimize your lead pipeline with real-time tracking.</p>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-3">
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-white dark:bg-navy border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/5 dark:hover:bg-cyan/10 font-medium rounded-lg transition-colors flex items-center gap-2 text-xs md:text-sm disabled:opacity-50"
                    >
                        <span className={`material-symbols-outlined text-base ${isRefreshing ? 'animate-spin' : ''}`}>refresh</span>
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowImport(true)}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-white dark:bg-navy border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/5 dark:hover:bg-cyan/10 font-medium rounded-lg transition-colors flex items-center gap-2 text-xs md:text-sm"
                    >
                        <span className="material-symbols-outlined text-base">upload_file</span>
                        Import
                    </button>
                    <button
                        onClick={() => setShowQuickAdd(true)}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-primary dark:bg-cyan hover:bg-primary/80 dark:hover:bg-cyan/80 text-white dark:text-navy font-bold rounded-lg shadow-[0_0_15px_rgba(37,176,155,0.3)] dark:shadow-[0_0_15px_rgba(8,217,214,0.3)] hover:shadow-[0_0_20px_rgba(37,176,155,0.5)] dark:hover:shadow-[0_0_20px_rgba(8,217,214,0.5)] transition-all flex items-center gap-2 text-xs md:text-sm"
                    >
                        <span className="material-symbols-outlined text-base">person_add</span>
                        Add Lead
                    </button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className={`rounded-xl border overflow-hidden mb-6 ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-teal/20 shadow-lg'}`}>
                <div className="p-3 md:p-4 flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap gap-2 md:gap-4 items-stretch sm:items-center">

                    {/* Search */}
                    <div className="relative group flex-1 min-w-0 sm:min-w-[240px]">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-darkTeal/40 dark:text-light/40 group-focus-within:text-teal dark:group-focus-within:text-cyan transition-colors text-xl">search</span>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            placeholder="Search by name, email, or property..."
                            className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:focus:ring-cyan focus:border-primary dark:focus:border-cyan text-darkTeal dark:text-light placeholder-darkTeal/40 dark:placeholder-light/30 transition-all"
                        />
                    </div>

                    <div className="flex gap-2 md:gap-3 w-full lg:w-auto overflow-x-auto pb-1 lg:pb-0">
                        {/* Status Filter */}
                        <div className="relative min-w-[130px] md:min-w-[160px]">
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg py-2.5 pl-4 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:focus:ring-cyan focus:border-primary dark:focus:border-cyan text-darkTeal dark:text-light cursor-pointer appearance-none font-medium"
                            >
                                <option value="" className="bg-white dark:bg-navy">All Statuses</option>
                                <option value="new" className="bg-white dark:bg-navy">New</option>
                                <option value="contacted" className="bg-white dark:bg-navy">Contacted</option>
                                <option value="qualified" className="bg-white dark:bg-navy">Qualified</option>
                                <option value="unqualified" className="bg-white dark:bg-navy">Unqualified</option>
                                <option value="closed" className="bg-white dark:bg-navy">Closed</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-darkTeal/40 dark:text-light/30 pointer-events-none">expand_more</span>
                        </div>

                        {/* Source Filter */}
                        <div className="relative min-w-[130px] md:min-w-[160px]">
                            <select
                                value={filters.source}
                                onChange={(e) => handleFilterChange('source', e.target.value)}
                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg py-2.5 pl-4 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:focus:ring-cyan focus:border-primary dark:focus:border-cyan text-darkTeal dark:text-light cursor-pointer appearance-none font-medium"
                            >
                                <option value="" className="bg-white dark:bg-navy">All Sources</option>
                                <option value="zillow" className="bg-white dark:bg-navy">Zillow</option>
                                <option value="realtor" className="bg-white dark:bg-navy">Realtor</option>
                                <option value="whatsapp" className="bg-white dark:bg-navy">WhatsApp</option>
                                <option value="facebook" className="bg-white dark:bg-navy">Facebook</option>
                                <option value="website" className="bg-white dark:bg-navy">Website</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-darkTeal/40 dark:text-light/30 pointer-events-none">expand_more</span>
                        </div>

                        {/* Date Range Filter */}
                        <div className="relative min-w-[150px] md:min-w-[180px]">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-darkTeal/40 dark:text-light/40 pointer-events-none text-xl">calendar_month</span>
                            <select
                                value={filters.dateRange}
                                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg py-2.5 pl-10 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:focus:ring-cyan focus:border-primary dark:focus:border-cyan text-darkTeal dark:text-light cursor-pointer appearance-none font-medium"
                            >
                                <option value="all" className="bg-white dark:bg-navy">All Time</option>
                                <option value="today" className="bg-white dark:bg-navy">Today</option>
                                <option value="last7days" className="bg-white dark:bg-navy">Last 7 Days</option>
                                <option value="last30days" className="bg-white dark:bg-navy">Last 30 Days</option>
                                <option value="last90days" className="bg-white dark:bg-navy">Last 90 Days</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-darkTeal/40 dark:text-light/30 pointer-events-none">expand_more</span>
                        </div>

                        {/* Hot Leads Only Toggle */}
                        <button
                            onClick={() => handleFilterChange('hotOnly', !filters.hotOnly)}
                            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-bold border transition-all whitespace-nowrap shrink-0 ${filters.hotOnly
                                ? 'bg-red-500/15 border-red-500/40 text-red-400 shadow-[0_0_10px_rgba(239,68,68,0.15)]'
                                : 'bg-mint dark:bg-navy border-teal/20 dark:border-white/10 text-darkTeal/60 dark:text-light/40 hover:border-red-500/30 hover:text-red-400'
                                }`}
                        >
                            <span className="text-base">🔥</span>
                            Hot Only
                        </button>
                    </div>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="mb-6">
                    <ErrorState message={error} onRetry={fetchLeads} />
                </div>
            )}

            {/* Leads Table */}
            {!error && (
                <div className="hidden md:block bg-white dark:bg-dark-800 rounded-xl border border-teal/20 dark:border-white/5 shadow-2xl mb-6">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm border-collapse min-w-[800px]">
                            <thead className="bg-mint/50 dark:bg-dark-900/50 text-darkTeal/50 dark:text-light/40 uppercase text-[10px] font-bold tracking-[0.15em] border-b border-teal/10 dark:border-white/5">
                                <tr>
                                    <th className="px-6 py-4 w-12 text-center">
                                        <input
                                            type="checkbox"
                                            checked={leads.length > 0 && selectedLeads.length === leads.length}
                                            onChange={toggleAllSelection}
                                            className="w-4 h-4 rounded border-teal/20 dark:border-white/20 bg-white dark:bg-navy text-primary dark:text-cyan focus:ring-primary dark:focus:ring-cyan focus:ring-offset-white dark:focus:ring-offset-navy transition-all cursor-pointer"
                                        />
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('lead_name')}>
                                        <span className="inline-flex items-center">Lead Information<SortIcon column="lead_name" /></span>
                                    </th>
                                    <th className="px-4 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('score')}>
                                        <span className="inline-flex items-center">Score<SortIcon column="score" /></span>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('property_address')}>
                                        <span className="inline-flex items-center">Property Address<SortIcon column="property_address" /></span>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('source_platform')}>
                                        <span className="inline-flex items-center">Source<SortIcon column="source_platform" /></span>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('status')}>
                                        <span className="inline-flex items-center">Status<SortIcon column="status" /></span>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('response_time_seconds')}>
                                        <span className="inline-flex items-center">Response<SortIcon column="response_time_seconds" /></span>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer select-none hover:text-primary dark:hover:text-cyan transition-colors" onClick={() => handleSort('created_at')}>
                                        <span className="inline-flex items-center">Date<SortIcon column="created_at" /></span>
                                    </th>
                                    <th className="px-6 py-4 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-teal/10 dark:divide-white/5">
                                {loading ? (
                                    // Skeleton Loader
                                    [...Array(5)].map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            <td className="px-6 py-5 text-center"><div className="w-4 h-4 rounded bg-teal/10 dark:bg-white/5 mx-auto"></div></td>
                                            <td className="px-6 py-5">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-lg bg-teal/10 dark:bg-white/5"></div>
                                                    <div className="space-y-2">
                                                        <div className="w-32 h-3 bg-teal/10 dark:bg-white/5 rounded"></div>
                                                        <div className="w-24 h-2 bg-teal/10 dark:bg-white/5 rounded"></div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5"><div className="w-40 h-3 bg-teal/10 dark:bg-white/5 rounded"></div></td>
                                            <td className="px-6 py-5"><div className="w-20 h-6 bg-teal/10 dark:bg-white/5 rounded-md"></div></td>
                                            <td className="px-6 py-5"><div className="w-24 h-6 bg-teal/10 dark:bg-white/5 rounded-full"></div></td>
                                            <td className="px-6 py-5"><div className="w-12 h-3 bg-teal/10 dark:bg-white/5 rounded"></div></td>
                                            <td className="px-6 py-5"><div className="w-16 h-3 bg-teal/10 dark:bg-white/5 rounded"></div></td>
                                            <td className="px-6 py-5"><div className="flex justify-end gap-2"><div className="w-8 h-8 bg-teal/10 dark:bg-white/5 rounded-lg"></div></div></td>
                                        </tr>
                                    ))
                                ) : leads.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="p-0 border-none bg-mint/50 dark:bg-dark-900/50">
                                            <EmptyState
                                                icon="group"
                                                title="No leads found"
                                                description="You haven't received any leads yet or none match your filters."
                                                buttonText="Import Leads"
                                                onAction={() => setShowImport(true)}
                                                simple={true}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    leads.map((lead) => {
                                        const sourceStyle = getSourceIcon(lead.source_platform);
                                        return (
                                            <tr
                                                key={lead.id || lead._id}
                                                onClick={() => navigate(`/leads/${lead.id || lead._id}`)}
                                                className="hover:bg-teal/5 dark:hover:bg-white/5 transition-colors group cursor-pointer"
                                            >
                                                <td className="px-6 py-5 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedLeads.includes(lead.id || lead._id)}
                                                        onChange={() => toggleSelection(lead.id || lead._id)}
                                                        className="w-4 h-4 rounded border-teal/20 dark:border-white/20 bg-white dark:bg-navy text-primary dark:text-cyan focus:ring-primary dark:focus:ring-cyan focus:ring-offset-white dark:focus:ring-offset-navy transition-all cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-6 py-5">
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative">
                                                            <div className="w-10 h-10 rounded-lg bg-teal/10 dark:bg-purple-500/20 flex items-center justify-center text-teal dark:text-purple-400 font-bold border border-teal/20 dark:border-purple-500/30 uppercase">
                                                                {lead.lead_name ? lead.lead_name.slice(0, 2) : 'NA'}
                                                            </div>
                                                            <span className={`absolute -bottom-1 -right-1 w-3 h-3 border-2 border-white dark:border-dark-800 rounded-full ${lead.status === 'new' ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-darkTeal dark:text-light group-hover:text-primary dark:group-hover:text-cyan transition-colors">{lead.lead_name || 'Unknown Lead'}</p>
                                                            <div className="flex flex-col text-[11px] text-darkTeal/50 dark:text-light/40 font-medium space-y-0.5">
                                                                <span className="truncate max-w-[150px]">{lead.lead_email}</span>
                                                                <span>{lead.lead_phone}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {/* Lead Score Badge */}
                                                <td className="px-4 py-5">
                                                    {lead.lead_score && (
                                                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold border shadow-sm ${getScoreBadgeStyle(lead.lead_score.tier)}`}>
                                                            <span className="text-sm">{lead.lead_score.icon}</span>
                                                            <span className="uppercase tracking-wider text-[10px]">{lead.lead_score.tier}</span>
                                                            <span className="opacity-60 text-[10px]">({lead.lead_score.score})</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-5">
                                                    <p className="text-darkTeal/80 dark:text-light/80 font-medium truncate max-w-[180px]">
                                                        {lead.property_address || 'No Property Address'}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase border ${sourceStyle.bg} ${sourceStyle.color}`}>
                                                        <span className="material-symbols-outlined text-[14px]">{sourceStyle.icon}</span>
                                                        {lead.source_platform}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(lead.status)}`}>
                                                        {lead.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <div className="flex items-center gap-1.5 text-primary dark:text-cyan font-bold">
                                                        <span className="material-symbols-outlined text-[16px]">bolt</span>
                                                        <span>{formatResponseTime(lead.response_time_seconds)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className="text-darkTeal/60 dark:text-light/50 text-xs font-medium">
                                                        {lead.created_at ? formatDateWithTimezone(lead.created_at, user?.timezone, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button onClick={() => navigate(`/leads/${lead.id || lead._id}`)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-teal/5 dark:hover:bg-white/10 text-darkTeal/40 dark:text-light/40 hover:text-primary dark:hover:text-cyan transition-all" title="View"><span className="material-symbols-outlined text-lg">visibility</span></button>
                                                        {lead.lead_phone && (
                                                            <a href={`tel:${lead.lead_phone}`} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-teal/5 dark:hover:bg-white/10 text-darkTeal/40 dark:text-light/40 hover:text-green-500 transition-all" title="Call"><span className="material-symbols-outlined text-lg">call</span></a>
                                                        )}
                                                        {lead.lead_phone && (
                                                            <a href={`https://wa.me/${lead.lead_phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-teal/5 dark:hover:bg-white/10 text-darkTeal/40 dark:text-light/40 hover:text-green-600 transition-all" title="WhatsApp"><span className="material-symbols-outlined text-lg">chat</span></a>
                                                        )}

                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Mobile Card View (md:hidden) */}
            {!error && (
                <div className="block md:hidden space-y-3 mb-6">
                    {loading ? (
                        // Mobile Skeletons
                        [...Array(3)].map((_, i) => (
                            <div key={i} className="bg-white dark:bg-dark-800 p-4 rounded-xl border border-teal/20 dark:border-white/5 shadow-sm animate-pulse space-y-3">
                                <div className="flex justify-between items-start">
                                    <div className="space-y-2 w-2/3">
                                        <div className="w-2/3 h-4 bg-teal/10 dark:bg-white/5 rounded"></div>
                                        <div className="w-1/2 h-3 bg-teal/10 dark:bg-white/5 rounded"></div>
                                    </div>
                                    <div className="w-16 h-5 bg-teal/10 dark:bg-white/5 rounded-full"></div>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-teal/5 dark:border-white/5">
                                    <div className="w-20 h-3 bg-teal/10 dark:bg-white/5 rounded"></div>
                                    <div className="w-20 h-3 bg-teal/10 dark:bg-white/5 rounded"></div>
                                </div>
                            </div>
                        ))
                    ) : leads.length === 0 ? (
                        <div className="bg-white dark:bg-dark-800 rounded-xl border border-teal/20 dark:border-white/5 shadow-sm overflow-hidden mb-4 py-4">
                            <EmptyState
                                icon="group"
                                title="No leads found"
                                description="Try clearing your filters or adding a new lead."
                                buttonText="Clear Filters"
                                onAction={() => setFilters({ search: '', status: '', source: '', dateRange: 'last30days', hotOnly: false })}
                                simple={true}
                            />
                        </div>
                    ) : (
                        leads.map((lead) => {
                            const sourceStyle = getSourceIcon(lead.source_platform);
                            return (
                                <div
                                    key={lead.id || lead._id}
                                    onClick={() => navigate(`/leads/${lead.id || lead._id}`)}
                                    className="bg-white dark:bg-dark-800 p-4 rounded-xl border border-teal/20 dark:border-white/5 shadow-sm active:scale-[0.98] transition-transform relative overflow-hidden"
                                >
                                    <div className="flex items-center justify-between gap-2 mb-3">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-10 h-10 rounded-lg bg-teal/10 dark:bg-purple-500/20 flex items-center justify-center text-teal dark:text-purple-400 font-bold border border-teal/20 dark:border-purple-500/30 uppercase text-sm shrink-0">
                                                {lead.lead_name ? lead.lead_name.slice(0, 2) : 'NA'}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-bold text-darkTeal dark:text-light text-sm truncate text-left">{lead.lead_name || 'Unknown Lead'}</p>
                                                <div className="flex items-center gap-1 text-[10px] text-darkTeal/50 dark:text-light/40 font-medium text-left">
                                                    <span className="truncate">{lead.lead_email || lead.lead_phone || 'No contact info'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {lead.lead_score && (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold border ${getScoreBadgeStyle(lead.lead_score.tier)}`}>
                                                    <span className="text-xs">{lead.lead_score.icon}</span>
                                                    <span className="uppercase">{lead.lead_score.tier}</span>
                                                </span>
                                            )}
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border shrink-0 ${getStatusColor(lead.status)}`}>
                                                {lead.status}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-start gap-4 pt-3 border-t border-teal/5 dark:border-white/5 overflow-x-auto no-scrollbar">
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase border ${sourceStyle.bg} ${sourceStyle.color}`}>
                                                <span className="material-symbols-outlined text-[10px]">{sourceStyle.icon}</span>
                                                {lead.source_platform}
                                            </span>
                                            {lead.response_time_seconds != null && (
                                                <span className="flex items-center gap-0.5 text-[9px] text-primary dark:text-cyan font-bold">
                                                    <span className="material-symbols-outlined text-[10px]">bolt</span>
                                                    {formatResponseTime(lead.response_time_seconds)}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[10px] font-medium text-darkTeal/40 dark:text-light/30 shrink-0">
                                            {lead.created_at ? formatDateWithTimezone(lead.created_at, user?.timezone, { month: 'short', day: 'numeric' }) : '-'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Pagination */}
            {leads.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-teal/10 dark:border-white/5 pt-4 md:pt-6 pb-8 md:pb-12">
                    <p className="text-sm font-medium text-darkTeal/50 dark:text-light/40">
                        Showing <span className="text-primary dark:text-cyan font-bold">{((pagination.currentPage - 1) * pagination.perPage) + 1}-{Math.min(pagination.currentPage * pagination.perPage, pagination.totalLeads)}</span> of <span className="text-darkTeal dark:text-light font-bold">{pagination.totalLeads}</span> leads
                    </p>
                    <div className="flex gap-1.5">
                        <button
                            disabled={pagination.currentPage === 1}
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            className="w-10 h-10 flex items-center justify-center rounded-lg border border-teal/20 dark:border-white/10 bg-white dark:bg-dark-800 text-darkTeal/50 dark:text-light/50 hover:bg-mint dark:hover:bg-white/5 hover:text-primary dark:hover:text-cyan transition-all group disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-xl">chevron_left</span>
                        </button>

                        {/* Simple Pagination Logic - can be expanded for complex ranges */}
                        {[...Array(Math.min(5, pagination.totalPages))].map((_, idx) => {
                            let pageNum = idx + 1;
                            // Logic to show pages around current page could be added here
                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`w-10 h-10 flex items-center justify-center rounded-lg font-bold transition-all ${pagination.currentPage === pageNum
                                        ? 'bg-primary dark:bg-cyan text-white dark:text-navy shadow-lg shadow-teal/20 dark:shadow-cyan/20'
                                        : 'border border-teal/20 dark:border-white/10 bg-white dark:bg-dark-800 text-darkTeal/80 dark:text-light/80 hover:bg-mint dark:hover:bg-white/5 hover:text-primary dark:hover:text-cyan'}`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            disabled={pagination.currentPage === pagination.totalPages}
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            className="w-10 h-10 flex items-center justify-center rounded-lg border border-teal/20 dark:border-white/10 bg-white dark:bg-dark-800 text-darkTeal/50 dark:text-light/50 hover:bg-mint dark:hover:bg-white/5 hover:text-primary dark:hover:text-cyan transition-all group disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-xl">chevron_right</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Actions Bar */}
            {selectedLeads.length > 0 && (
                <div className="fixed bottom-20 md:bottom-8 left-4 md:left-[calc(16rem+2rem)] right-4 md:right-8 z-[60] animate-in slide-in-from-bottom-10 duration-500">
                    <div className="bg-dark-900 border border-teal/30 dark:border-cyan/30 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col sm:flex-row items-center justify-between backdrop-blur-xl bg-opacity-95 ring-1 ring-teal/10 dark:ring-cyan/10 gap-4 sm:gap-0">
                        <div className="flex items-center gap-6 px-2">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary dark:bg-cyan flex items-center justify-center text-white dark:text-navy font-black text-xs">
                                    {selectedLeads.length}
                                </div>
                                <p className="text-sm font-bold text-light">Leads Selected</p>
                            </div>
                            <div className="h-6 w-px bg-white/10 hidden sm:block"></div>
                            <div className="flex items-center gap-4">
                                <button onClick={handleBulkMarkContacted} className="flex items-center gap-2 text-xs font-black text-light/60 hover:text-primary dark:hover:text-cyan transition-all uppercase tracking-widest">
                                    <span className="material-symbols-outlined text-lg">check_circle</span>
                                    <span className="hidden sm:inline">Mark Contacted</span>
                                </button>
                                <button onClick={handleBulkExport} className="flex items-center gap-2 text-xs font-black text-light/60 hover:text-primary dark:hover:text-cyan transition-all uppercase tracking-widest">
                                    <span className="material-symbols-outlined text-lg">ios_share</span>
                                    <span className="hidden sm:inline">Bulk Export</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                            <button
                                onClick={() => setSelectedLeads([])}
                                className="px-5 py-2 text-[11px] font-black uppercase tracking-widest text-light/40 hover:text-light transition-all"
                            >
                                Cancel
                            </button>
                            <button onClick={handleBulkDelete} className="px-6 py-2.5 bg-pink/10 border border-pink/30 text-pink hover:bg-pink/20 font-black rounded-xl transition-all flex items-center gap-2 text-[11px] uppercase tracking-widest">
                                <span className="material-symbols-outlined text-lg">delete_sweep</span>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Add Lead Modal */}
            <QuickAddLead
                isOpen={showQuickAdd}
                onClose={() => setShowQuickAdd(false)}
                onSuccess={() => {
                    fetchLeads();
                }}
            />

            {/* Import Leads Modal */}
            <ImportLeadsModal
                isOpen={showImport}
                onClose={() => setShowImport(false)}
                onSuccess={() => {
                    fetchLeads();
                }}
            />
        </div>
    );
};

export default Leads;
